import { Player, Position, PositionPreference, PositionPreferenceSets } from '../types';

export interface NormalizedPositionPreferences {
  preferences: PositionPreference[];
  variant: 'indoor' | 'outdoor';
  variants: PositionPreferenceSets;
  hasExplicitPriorities: boolean;
}

interface NormalizedPreferenceArrayResult {
  values: PositionPreference[];
  hadExplicit: boolean;
}

const normalizePreferenceArray = (positions: unknown): NormalizedPreferenceArrayResult => {
  if (!Array.isArray(positions)) {
    return { values: [], hadExplicit: false };
  }

  let hadExplicit = false;

  const values = (positions as unknown[])
    .map((p, index) => {
      if (p && typeof p === 'object' && 'position' in p) {
        const position = (p as { position: Position | null | undefined }).position;
        if (typeof position !== 'string' || !position.trim()) return null;
        const priorityValue = (p as { priority?: number }).priority;
        if (typeof priorityValue === 'number' && Number.isFinite(priorityValue)) {
          hadExplicit = true;
          return { position, priority: priorityValue };
        }
        return { position, priority: index };
      }
      if (typeof p === 'string') {
        return { position: p as Position, priority: index };
      }
      return null;
    })
    .filter((value): value is PositionPreference => value !== null)
    .sort((a, b) => a.priority - b.priority)
    .map((pref, idx) => ({ ...pref, priority: idx }));

  return { values, hadExplicit };
};

const coerceVariant = (value: unknown): 'indoor' | 'outdoor' | null => {
  if (value === 'indoor' || value === 'outdoor') return value;
  return null;
};

const emptyPreferenceSets = (): PositionPreferenceSets => ({
  indoor: [],
  outdoor: []
});

/**
 * Returns a lean representation of a player for storing in
 * substitution schedules and substitutions. Irrelevant fields
 * like statistics or evaluations are stripped out so that only
 * the essentials remain.
 */
export const sanitizePlayerForSchedule = (player: Player): Player => {
  const variant = player.positionPreferenceVariant ?? 'outdoor';
  const variants = player.positionPreferencesByVariant ?? {
    indoor: variant === 'indoor' ? player.positionPreferences.slice() : [],
    outdoor: variant === 'outdoor' ? player.positionPreferences.slice() : []
  };
  const normalizedVariant = normalizePreferenceArray(variants[variant]).values;

  return {
    id: player.id,
    clubId: player.clubId,
    name: player.name,
    number: player.number,
    positionPreferences: normalizedVariant,
    positionPreferenceVariant: variant,
    positionPreferencesByVariant: {
      indoor: normalizePreferenceArray(variants.indoor).values,
      outdoor: normalizePreferenceArray(variants.outdoor).values
    },
    matchPositionOverride: player.matchPositionOverride,
    isGoalkeeper: player.isGoalkeeper
  };
};

/** Keep only the position fields from an explicit match snapshot over canonical player data. */
export const mergeMatchPositionOverride = (
  canonical: Player | undefined,
  override: Player,
): Player => ({
  ...(canonical || override),
  positionPreferences: override.positionPreferences,
  positionPreferenceVariant: override.positionPreferenceVariant,
  positionPreferencesByVariant: override.positionPreferencesByVariant,
  hasPositionPriority: override.hasPositionPriority,
  matchPositionOverride: true,
});

/** Prefer an earlier match override when a later canonical roster source has the same player. */
export const preferMatchPositionOverride = (
  current: Player | undefined,
  incoming: Player,
): Player => current?.matchPositionOverride && !incoming.matchPositionOverride
  ? mergeMatchPositionOverride(incoming, current)
  : incoming;

/**
 * Normalizes a positions array from the database into a sorted
 * list of position preference objects. Supports both legacy
 * string arrays and the new object format with priorities.
 */
export const normalizePositionPreferences = (positions: unknown): NormalizedPositionPreferences => {
  if (positions && typeof positions === 'object' && !Array.isArray(positions)) {
    const raw = positions as Record<string, unknown>;
    const indoor = normalizePreferenceArray(raw.indoor);
    const outdoor = normalizePreferenceArray(raw.outdoor);
    const variants: PositionPreferenceSets = {
      indoor: indoor.values,
      outdoor: outdoor.values
    };

    const requestedVariant = coerceVariant(raw.variant ?? raw.selected ?? raw.mode);
    const variant: 'indoor' | 'outdoor' = requestedVariant
      ?? (variants.outdoor.length > 0
        ? 'outdoor'
        : variants.indoor.length > 0
          ? 'indoor'
          : 'outdoor');

    return {
      preferences: variants[variant],
      variant,
      variants,
      hasExplicitPriorities: variant === 'indoor' ? indoor.hadExplicit : outdoor.hadExplicit
    };
  }

  const legacy = normalizePreferenceArray(positions);
  return {
    preferences: legacy.values,
    variant: 'outdoor',
    variants: {
      indoor: [],
      outdoor: legacy.values
    },
    hasExplicitPriorities: legacy.hadExplicit
  };
};

/** Return only the preferences that belong to the formation variant being viewed. */
export const getPositionPreferencesForVariant = (
  player: Pick<Player, 'positionPreferences' | 'positionPreferenceVariant' | 'positionPreferencesByVariant'>,
  variant: 'indoor' | 'outdoor'
): PositionPreference[] => {
  if (player.positionPreferencesByVariant) {
    return normalizePreferenceArray(player.positionPreferencesByVariant[variant]).values;
  }

  return (player.positionPreferenceVariant ?? 'outdoor') === variant
    ? normalizePreferenceArray(player.positionPreferences).values
    : [];
};

/** Align presentation and planning to one variant, including an explicit empty set. */
export const withPositionPreferenceVariant = (player: Player, variant: 'indoor' | 'outdoor'): Player => {
  const preferences = getPositionPreferencesForVariant(player, variant)
    .slice().sort((a, b) => a.priority - b.priority)
    .map((preference, priority) => ({ ...preference, priority }));
  if (player.positionPreferenceVariant === variant
    && player.positionPreferences.length === preferences.length
    && player.positionPreferences.every((preference, index) => preference.position === preferences[index].position
      && preference.priority === preferences[index].priority)) return player;
  return { ...player, positionPreferenceVariant: variant, positionPreferences: preferences };
};

/** Group eligible players without letting duplicate preferences inflate a position count. */
export const groupPlayersByDistinctPreferredPosition = (
  players: Player[],
  resolvePosition: (key: string) => string | undefined = key => key
): Record<string, Player[]> => {
  const groups: Record<string, Player[]> = Object.create(null);

  players.forEach(player => {
    const distinctPositions = new Set(
      (player.positionPreferences || []).map(preference => resolvePosition(preference.position))
    );
    distinctPositions.forEach(position => {
      if (position === undefined) return;
      (groups[position] ||= []).push(player);
    });
  });

  return groups;
};

export const serializePositionPreferenceSets = (
  source: Pick<
    Partial<Player>,
    'positionPreferences' | 'positionPreferenceVariant' | 'positionPreferencesByVariant'
  >
): PositionPreferenceSets => {
  const sets = emptyPreferenceSets();
  const variant = source.positionPreferenceVariant ?? 'outdoor';

  if (source.positionPreferencesByVariant) {
    sets.indoor = normalizePreferenceArray(source.positionPreferencesByVariant.indoor).values;
    sets.outdoor = normalizePreferenceArray(source.positionPreferencesByVariant.outdoor).values;
  }

  if (source.positionPreferences?.length) {
    const normalizedActive = normalizePreferenceArray(source.positionPreferences).values;
    if (variant === 'indoor') {
      sets.indoor = normalizedActive;
    } else {
      sets.outdoor = normalizedActive;
    }
  }

  return sets;
};

export const withNormalizedPositionPreferences = <T extends Partial<Player>>(
  player: T
): T & {
  positionPreferences: PositionPreference[];
  positionPreferenceVariant: 'indoor' | 'outdoor';
  positionPreferencesByVariant: PositionPreferenceSets;
} => {
  const sets = serializePositionPreferenceSets(player);
  const normalized = normalizePositionPreferences({
    ...sets,
    variant: player.positionPreferenceVariant ?? 'outdoor'
  });

  return {
    ...player,
    positionPreferences: normalized.preferences,
    positionPreferenceVariant: normalized.variant,
    positionPreferencesByVariant: sets
  } as T & {
    positionPreferences: PositionPreference[];
    positionPreferenceVariant: 'indoor' | 'outdoor';
    positionPreferencesByVariant: PositionPreferenceSets;
  };
};
